import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Grid as MuiGrid,
  MenuItem,
  Radio,
  RadioGroup,
  Paper,
  Chip,
  Divider,
  InputAdornment
} from '@mui/material';
import { 
  Add as AddIcon, 
  Home as HomeIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Notes as NotesIcon,
  MailOutline as MailIcon
} from '@mui/icons-material';
import { addressApi } from '../../api/auth';
import { Address, AddressCreate, AddressUpdate } from '../../types/user';
import { useAuth } from '../../context/AuthContext';

// Define Grid component with proper React memo to prevent hooks issues
const Grid = React.memo(function Grid(props: any) {
  return <MuiGrid {...props} />;
});

interface AddressSelectorProps {
  onAddressSelected: (address: Address) => void;
  onCancel?: () => void;
  buttonText?: string;
}

const AddressSelector: React.FC<AddressSelectorProps> = ({
  onAddressSelected,
  onCancel,
  buttonText = 'Continue with this Address'
}) => {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [formData, setFormData] = useState<AddressCreate>({
    name: '',
    street: '',
    city: '',
    country_code: 'CA',
    postal_code: '',
    state_or_province_code: 'BC',
    phone: '',
    email: user?.email || '',
    is_default: false,
    delivery_notes: ''
  });
  
  useEffect(() => {
    fetchAddresses();
  }, []);
  
  // Update email whenever user changes
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({
        ...prev,
        email: user.email
      }));
    }
  }, [user]);
  
  const fetchAddresses = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await addressApi.getAddresses();
      setAddresses(data);
      
      // Auto-select default address
      const defaultAddress = data.find(addr => addr.is_default);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
      } else if (data.length > 0) {
        setSelectedAddressId(data[0].id);
      } else {
        // If no addresses, show the add form
        setShowAddForm(true);
      }
    } catch (err: any) {
      console.error('Error fetching addresses:', err);
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'object') {
          setError(JSON.stringify(err.response.data.detail));
        } else {
          setError(String(err.response.data.detail));
        }
      } else {
        setError('Failed to fetch addresses');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_default' ? checked : value
    }));
  };
  
  const handleShowAddForm = () => {
    setFormData({
      name: '',
      street: '',
      city: '',
      country_code: 'CA',
      postal_code: '',
      state_or_province_code: 'BC',
      phone: '',
      email: user?.email || '',
      is_default: addresses.length === 0, // Make default if first address
      delivery_notes: ''
    });
    setShowAddForm(true);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      // Ensure email is set to user's email
      const addressData = {
        ...formData,
        email: user?.email || ''
      };
      
      // Create new address
      const newAddress = await addressApi.createAddress(addressData);
      setSuccess('Address created successfully');
      
      // Refresh address list
      await fetchAddresses();
      
      // Select the newly created address
      setSelectedAddressId(newAddress.id);
      setShowAddForm(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Address error:', err);
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'object') {
          setError(JSON.stringify(err.response.data.detail));
        } else {
          setError(String(err.response.data.detail));
        }
      } else {
        setError('Failed to save address');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedAddressId(Number(event.target.value));
  };
  
  const handleContinue = () => {
    const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);
    if (selectedAddress) {
      onAddressSelected(selectedAddress);
    }
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {loading && addresses.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : showAddForm ? (
        <Box 
          component="form" 
          onSubmit={handleSubmit}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            p: { xs: 2, sm: 3 },
            position: 'relative',
            maxWidth: 700,
            mx: 'auto'
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
            Add a New Address
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Contact Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField
                  required
                  fullWidth
                  id="name"
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="John Smith"
                  InputProps={{
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  id="phone"
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="(604) 555-1234"
                  InputProps={{
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Address Details
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="street"
                  label="Street Address"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="123 Main Street, Apt 4B"
                  InputProps={{
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="city"
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  variant="outlined"
                  InputProps={{
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="postal_code"
                  label="Postal Code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="V6B 3K9"
                  InputProps={{
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="state_or_province_code"
                  label="Province"
                  name="state_or_province_code"
                  value={formData.state_or_province_code}
                  onChange={handleChange}
                  select
                  variant="outlined"
                  InputProps={{
                    sx: { borderRadius: 1 }
                  }}
                >
                  <MenuItem value="BC">British Columbia</MenuItem>
                  <MenuItem value="AB">Alberta</MenuItem>
                  <MenuItem value="ON">Ontario</MenuItem>
                  <MenuItem value="QC">Quebec</MenuItem>
                  <MenuItem value="MB">Manitoba</MenuItem>
                  <MenuItem value="SK">Saskatchewan</MenuItem>
                  <MenuItem value="NS">Nova Scotia</MenuItem>
                  <MenuItem value="NB">New Brunswick</MenuItem>
                  <MenuItem value="NL">Newfoundland and Labrador</MenuItem>
                  <MenuItem value="PE">Prince Edward Island</MenuItem>
                  <MenuItem value="YT">Yukon</MenuItem>
                  <MenuItem value="NT">Northwest Territories</MenuItem>
                  <MenuItem value="NU">Nunavut</MenuItem>
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="country_code"
                  label="Country"
                  name="country_code"
                  value={formData.country_code}
                  onChange={handleChange}
                  select
                  variant="outlined"
                  InputProps={{
                    sx: { borderRadius: 1 }
                  }}
                >
                  <MenuItem value="CA">Canada</MenuItem>
                  <MenuItem value="US">United States</MenuItem>
                </TextField>
              </Grid>

            </Grid>
        </Box>

        <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <NotesIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="subtitle2" color="text.secondary">
            Delivery Notes
        </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={2}>
              
            <TextField
                fullWidth
                id="delivery_notes"
                label="Delivery Notes"
                name="delivery_notes"
                multiline
                rows={2}
                value={formData.delivery_notes}
                onChange={handleChange}
                variant="outlined"
                placeholder="Special delivery instructions, gate codes, preferred time, etc."
                InputProps={{
                sx: { borderRadius: 1 }
                }}
            />
        </Grid>
        </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_default}
                  onChange={handleChange}
                  name="is_default"
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <HomeIcon fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Set as default address
                  </Typography>
                </Box>
              }
            />
          </Box>
          
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            {addresses.length > 0 && (
              <Button 
                onClick={() => setShowAddForm(false)} 
                variant="outlined"
                sx={{ borderRadius: 1 }}
              >
                Back to Saved Addresses
              </Button>
            )}
            <Box>
              {onCancel && (
                <Button 
                  onClick={onCancel} 
                  sx={{ mr: 2 }}
                >
                  Cancel
                </Button>
              )}
              <Button 
                type="submit" 
                variant="contained" 
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ borderRadius: 1 }}
              >
                {loading ? 'Saving...' : 'Save Address'}
              </Button>
            </Box>
          </Box>
        </Box>
      ) : addresses.length > 0 ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Select a Saved Address or Add a New One
            </Typography>
            <Button 
              startIcon={<AddIcon />} 
              onClick={handleShowAddForm}
              variant="outlined"
              sx={{ borderRadius: 1 }}
            >
              Add New Address
            </Button>
          </Box>
          
          <RadioGroup
            value={selectedAddressId || ''}
            onChange={handleAddressChange}
          >
            <Grid container spacing={2}>
              {addresses.map((address) => (
                <Grid item xs={12} key={address.id}>
                  <Paper 
                    elevation={selectedAddressId === address.id ? 3 : 1}
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: selectedAddressId === address.id ? 'primary.main' : 'divider',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.light',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.08)'
                      },
                      width: '100%',
                      mx: 'auto'
                    }}
                    onClick={() => setSelectedAddressId(address.id)}
                  >
                    <Radio 
                      value={address.id} 
                      checked={selectedAddressId === address.id}
                      sx={{ 
                        position: 'absolute',
                        top: 16,
                        right: 16,
                      }}
                    />
                    
                    {address.is_default && (
                      <Chip 
                        label="Default" 
                        size="small" 
                        color="primary" 
                        icon={<HomeIcon />}
                        sx={{ 
                          position: 'absolute',
                          top: 16,
                          left: 16,
                          fontWeight: 500
                        }}
                      />
                    )}
                    
                    <Box sx={{ mt: address.is_default ? 4 : 2, mb: 0.5, pl: 1, pr: 5 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {address.name}
                      </Typography>
                      
                      <Box sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                        <div>{address.street}</div>
                        <div>{address.city}, {address.state_or_province_code} {address.postal_code}</div>
                        <div>{address.country_code === 'CA' ? 'Canada' : 'United States'}</div>
                        {address.phone && <div>Phone: {address.phone}</div>}
                        
                        {address.delivery_notes && (
                          <Box sx={{ mt: 1, fontSize: '0.85rem' }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                              Delivery Notes:
                            </Typography>
                            <Box sx={{ color: 'text.secondary' }}>{address.delivery_notes}</Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
          
          <Box sx={{ 
            mt: 3, 
            display: 'flex', 
            justifyContent: 'flex-end', 
            width: '100%'
          }}>
            <Button 
              onClick={onCancel} 
              variant="text"
              color="inherit"
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleContinue}
              disabled={selectedAddressId === null}
              sx={{ borderRadius: 1 }}
            >
              {buttonText}
            </Button>
          </Box>
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" gutterBottom>
            No Addresses Found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You don't have any saved addresses yet. Please add one to continue.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleShowAddForm}
            sx={{ borderRadius: 1 }}
          >
            Add an Address
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AddressSelector; 